import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import styledConst from "styles";
import { faker } from "@faker-js/faker";
import { Row, Col, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { get } from "lodash";
import { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";

import { ArrowIcon, BlueText, Box } from "components/common";
import { LDSBus } from "types/Gateways";
import refreshIcon from "assets/svg/refresh-green.svg";
import { getPowerStatusText, isFaulty } from "utils/gatewayFunctions";
import { ReactComponent as InfoSvg } from "assets/svg/info.svg";
import { LDSUTabPanel } from "constant";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import searchIcon from "assets/svg/search.svg";
import dltWhiteIcon from "assets/svg/delete-white.svg";
import Avatar from "@mui/material/Avatar";

const FaultyMessages = {
    "1": "Port is faulty because of hardware issue.",
    "2": "Port is faulty because port power exceeded.",
    "3": "Port is faulty because power exceeded in all ports. ",
};

interface LDSBusInfo {
    ldsus?: any[];
    sensors?: any[];
    actuators?: any[];
}

type LDSBusState = LDSBus & {
    refreshing: boolean;
    info?: LDSBusInfo;
};

const DeviceStatus = styled.div`
    display: inline-flex;
    margin-left: 20px;

    &.off {
        .status {
            color: ${styledConst.Secondary_Red};
        }
    }

    &.on {
        .status {
            color: ${styledConst.Secondary_Green};
        }
    }

    &.faulty {
        .status {
            color: ${styledConst.Orange};
        }
    }
`;

const LDSBusBox = styled(Box)`
    padding: 20px;

    .box-header {
        display: flex;
        align-items: center;

        .box-title {
            font-size: 1.125rem;
        }
    }

    &.on {
        background-color: #223228;
        border: 1px solid ${styledConst.Secondary_Green};
    }

    &.off {
        background-color: #382229;
        border: 1px solid ${styledConst.Primary_Red};
    }

    &.faulty {
        background-color: #40311c;
        border: 1px solid ${styledConst.Orange};
    }
`;

const InfoIcon = styled.span`
    margin-left: 1rem;
    display: inline-flex;

    path {
        fill: ${styledConst.Primary_Green_1};
    }
`;

const CustomTooltip = styled(Tooltip)`
    .tooltip-inner {
        background-color: #ffffff;
        color: #000000;
    }

    .arrow {
        &::before {
            border-top-color: #ffffff;
        }
    }
`;

const CustomButton = styled(Button)`
    @media (max-width: 1330px) {
        padding: 0.25rem 0.5rem;
        font-size: 0.875rem;
        line-height: 1.5;
        border-radius: 0.2rem;
    }
`;

const FaultyTooltip = ({ status }: { status: number }) => {
    const renderTooltip = (props: OverlayInjectedProps) => (
        <CustomTooltip id={faker.datatype.uuid()} {...props}>
            {get(FaultyMessages, status)}
        </CustomTooltip>
    );
    return (
        <OverlayTrigger
            placement="top"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip}
        >
            <InfoIcon>
                <InfoSvg />
            </InfoIcon>
        </OverlayTrigger>
    );
};

const LDSBusList = ({
    LDSUPowerState = [],
    gatewayId,
    gatewayName,
    onRefresh,
    openWarningConfirmationModal,
    openDeleteModal,
    openTogglePowerModal,
}: any) => {
    return LDSUPowerState.map(
        ({ id, power, status, refreshing, info }: LDSBusState) => (
            <LDSBusBox
                key={id}
                className={`${getPowerStatusText(power, status).toLowerCase()}`}
            >
                <div className="mb-4 box-header">
                    <Link
                        to={{
                            pathname: `/gateway-details/${gatewayId}/LDSBus/${id}`,
                            state: {
                                gatewayName,
                                portNumber: `${id}`,
                                UUID: gatewayId,
                            },
                        }}
                        className="box-title"
                    >
                        LDSBus {id}
                    </Link>
                    <DeviceStatus
                        className={`${getPowerStatusText(
                            power,
                            status
                        ).toLowerCase()}`}
                    >
                        <span className="status mr-1">
                            {getPowerStatusText(power, status)}
                        </span>
                        <Avatar
                            className={
                                refreshing
                                    ? "refresh-icon refresh-animation"
                                    : "refresh-icon"
                            }
                            src={refreshIcon}
                            alt="refresh"
                            onClick={() => {
                                onRefresh(id);
                            }}
                            onKeyDown={() => {
                                onRefresh(id);
                            }}
                        />
                    </DeviceStatus>
                    {isFaulty(status) && <FaultyTooltip status={status} />}
                    <Link
                        to={{
                            pathname: `/gateway-details/${gatewayId}/LDSBus/${id}`,
                            state: {
                                gatewayName,
                                portNumber: `${id}`,
                                UUID: gatewayId,
                            },
                        }}
                        className="ml-auto"
                    >
                        <ArrowIcon className="align-middle" />
                    </Link>
                </div>
                <Box className="mb-2 d-flex">
                    <div>
                        {get(info, "ldsus.length", 0)}{" "}
                        <BlueText className="ml-2 large">LDSUs</BlueText>
                    </div>
                    <Link
                        to={{
                            pathname: `/gateway-details/${gatewayId}/LDSBus/${id}`,
                            state: {
                                gatewayName,
                                portNumber: `${id}`,
                                UUID: gatewayId,
                            },
                        }}
                        className="ml-auto"
                    >
                        <ArrowIcon className="material-icons align-middle">
                            keyboard_arrow_right
                        </ArrowIcon>
                    </Link>
                </Box>
                <div className="mb-2">
                    <BlueText>
                        Sensors and Actuators reported from connected LDSUs
                    </BlueText>
                </div>
                <Row>
                    <Col>
                        <Box className="mb-2 d-flex">
                            <div>
                                {get(info, "sensors.length", 0)}{" "}
                                <BlueText className="ml-2 large">
                                    Sensors
                                </BlueText>
                            </div>
                            <Link
                                to={{
                                    pathname: `/gateway-details/${gatewayId}/LDSBus/${id}`,
                                    state: {
                                        gatewayName,
                                        portNumber: `${id}`,
                                        UUID: gatewayId,
                                        panel: LDSUTabPanel.Sensor,
                                    },
                                }}
                                className="ml-auto"
                            >
                                <ArrowIcon className="material-icons align-middle">
                                    keyboard_arrow_right
                                </ArrowIcon>
                            </Link>
                        </Box>
                    </Col>
                    <Col>
                        <Box className="mb-2 d-flex">
                            <div>
                                {get(info, "actuators.length", 0)}{" "}
                                <BlueText className="ml-2 large">
                                    Actuators
                                </BlueText>
                            </div>
                            <Link
                                to={{
                                    pathname: `/gateway-details/${gatewayId}/LDSBus/${id}`,
                                    state: {
                                        gatewayName,
                                        portNumber: `${id}`,
                                        UUID: gatewayId,
                                        panel: LDSUTabPanel.Actuator,
                                    },
                                }}
                                className="ml-auto"
                            >
                                <ArrowIcon className="material-icons align-middle">
                                    keyboard_arrow_right
                                </ArrowIcon>
                            </Link>
                        </Box>
                    </Col>
                </Row>
                <div className="d-flex flex-wrap">
                    <HoverAuthorizeTooltip
                        className="mr-2 mt-2"
                        permission="gateway:update"
                    >
                        <CustomButton
                            onClick={() => {
                                openWarningConfirmationModal({
                                    resource: "port",
                                    actionType: "scan",
                                    selectedPortToScan: id,
                                });
                            }}
                            variant="primary"
                        >
                            <span>
                                <img
                                    className="mr-2"
                                    src={searchIcon}
                                    alt="search"
                                />
                            </span>
                            {" "}Scan
                        </CustomButton>
                    </HoverAuthorizeTooltip>
                    <HoverAuthorizeTooltip
                        className="mr-2 mt-2"
                        permission="gateway:update"
                    >
                        <CustomButton
                            onClick={() => openDeleteModal(id)}
                            variant="danger"
                        >
                            <span className="d-flex align-items-center">
                                <img
                                    className="mr-2"
                                    src={dltWhiteIcon}
                                    alt="delete"
                                />
                                <span>Delete</span>
                            </span>
                        </CustomButton>
                    </HoverAuthorizeTooltip>
                    {!isFaulty(status) && (
                        <HoverAuthorizeTooltip
                            className="mt-2 ml-xl-auto"
                            permission="gateway:update"
                        >
                            <CustomButton
                                onClick={() => openTogglePowerModal(id, power)}
                                variant={`${power ? "danger" : "primary"}`}
                                className=""
                            >
                                Power {power ? "Off" : "On"}
                            </CustomButton>
                        </HoverAuthorizeTooltip>
                    )}
                </div>
            </LDSBusBox>
        )
    );
};

export default LDSBusList;
